import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { FileDown, Download, ArrowRight } from "lucide-react";

export default function Blog() {
  const { addItem } = useCart();
  const [posts, setPosts] = useState([]);
  const [journals, setJournals] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/products", { params: { product_type: "digital" } }).then((r) => setJournals(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <div className="max-w-2xl">
        <span className="text-xs tracking-[0.25em] uppercase text-[#8A8175] font-semibold">The Journal</span>
        <h1 className="font-heading text-5xl sm:text-6xl text-[#3A3632] mt-3">Gentle words &amp; gift-giving ideas.</h1>
        <p className="text-lg text-[#6B655F] mt-4 leading-relaxed">Encouragement, reflections, and thoughtful ways to show up for the people you love.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {posts.map((post, i) => (
          <Link key={post.id} to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}
            className="group bg-white rounded-xl border border-[#E5DFD5] overflow-hidden transition-all duration-500 hover:shadow-lg hover:-translate-y-1 gh-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="aspect-[16/10] bg-[#F2EFEB] overflow-hidden"><img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="p-6">
              <h3 className="font-heading text-2xl text-[#3A3632] leading-snug">{post.title}</h3>
              <p className="text-sm text-[#6B655F] mt-2 leading-relaxed line-clamp-3">{post.excerpt}</p>
              <span className="inline-block mt-4 text-sm text-[#8E9A86] gh-link-underline">Read more →</span>
            </div>
          </Link>
        ))}
      </div>

      {journals.length > 0 && (
        <section className="mt-20 border-t border-[#E5DFD5] pt-14" data-testid="featured-journals">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <span className="text-xs tracking-[0.25em] uppercase text-[#8A8175] font-semibold flex items-center gap-2"><FileDown className="h-4 w-4 text-[#8E9A86]" /> Featured Journals</span>
              <h2 className="font-heading text-3xl sm:text-4xl text-[#3A3632] mt-2">Printable journals to slow down with.</h2>
            </div>
            <Link to="/digital-downloads" data-testid="journals-view-all" className="text-sm text-[#8E9A86] gh-link-underline inline-flex items-center gap-1">View all printables <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {journals.map((p, i) => (
              <div key={p.id} data-testid={`journal-card-${p.slug}`} className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden transition-all duration-500 hover:shadow-lg hover:-translate-y-1 gh-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="aspect-[4/3] bg-[#F2EFEB] overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <h3 className="font-heading text-lg text-[#3A3632] leading-snug">{p.name}</h3>
                  <p className="text-sm text-[#6B655F] mt-1 leading-relaxed line-clamp-2">{p.short_desc}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[#8E9A86] font-semibold">{money(p.price)}</span>
                    <button onClick={() => { addItem(p, 1); toast.success("Added to cart 💛"); }} data-testid={`journal-add-${p.slug}`}
                      className="bg-[#C4A4A4] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-[#B09090] transition-colors inline-flex items-center gap-1.5">
                      <Download className="h-4 w-4" /> Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
